import React, { Dispatch, memo, SetStateAction, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import Button from '@components/buttons/Button';
import Portal from '@components/Portal/Portal';
import EditGeometryToolbar from '@components/map/EditGeometryToolbar/EditGeometryToolbar';
import { useToastContext } from '@context/toastContext';
import { useTranslation } from 'react-i18next';
import { useTypedSelector } from '@redux/Hooks/storeSelectors';
import { selectDrawnFeatures } from '@redux/selectors/selectors';
import { enableEditing } from '@redux/draw/draw-reducer';
import { FeatureProperties } from '@redux/map/types';
import { drawRef } from '@features/map/draw/DrawControl';
import { AvailableDrawTypes, DrawControlsClass } from '@utils/DrawControlsClass';
import { useLayersInteractivity } from '@hooks/map/useLayersInteractivity';
import '@components/form/GeometryField/geometry-form-field.scss';

type Props = {
    name: string;
    label: string;
    value?: string;
    initialValue?: string;
    drawMode: Exclude<AvailableDrawTypes, 'AllControls'>;
    required?: boolean;
    disabled?: boolean;
    active: boolean;
    onActivate: () => void;
    onDeactivate: () => void;
    onChange: (value: string) => void;
};

const DRAW_MODE_BY_TYPE: Record<Exclude<AvailableDrawTypes, 'AllControls'>, string> = {
    Point: 'draw_point',
    LineString: 'draw_line_string',
    Polygon: 'draw_polygon',
};

const MESSAGE_BY_TYPE: Record<Exclude<AvailableDrawTypes, 'AllControls'>, string> = {
    Point: 'forms.select-stop-message',
    LineString: 'forms.draw-line-message',
    Polygon: 'forms.draw-polygon-message',
};

const LambdaGeometryField = memo(
    ({
        name,
        label,
        value = '',
        initialValue = '',
        drawMode,
        required,
        disabled,
        active,
        onActivate,
        onDeactivate,
        onChange,
    }: Props) => {
        const inputRef = useRef<HTMLInputElement | null>(null);
        const onChangeRef = useRef(onChange);
        const [feature, setFeature] = useState(value);
        const drawnFeatures = useTypedSelector(selectDrawnFeatures) as GeoJSON.Feature<
            any,
            Partial<FeatureProperties>
        >[];
        const dispatch = useDispatch();
        const { addToast } = useToastContext();
        const { t } = useTranslation();
        const { recoverSettingsAfterDrawing } = useLayersInteractivity();

        useEffect(() => {
            setFeature(value);
        }, [value]);

        useEffect(() => {
            onChangeRef.current = onChange;
        }, [onChange]);

        useEffect(() => {
            inputRef.current?.setCustomValidity(required && !feature ? 'valueMissing' : '');
            onChangeRef.current(feature || '');
        }, [feature, required]);

        useEffect(() => {
            dispatch(enableEditing(true));

            return () => {
                DrawControlsClass.setDrawControlVisibility({
                    drawType: 'AllControls',
                    visible: false,
                });
                recoverSettingsAfterDrawing();
                dispatch(enableEditing(false));
            };
        }, [dispatch, recoverSettingsAfterDrawing]);

        const startDrawing = useCallback(() => {
            if (disabled || feature) {
                return;
            }

            onActivate();
            addToast(t(MESSAGE_BY_TYPE[drawMode]), 'info');
            DrawControlsClass.setDrawControlVisibility({ drawType: drawMode, visible: true });
            drawRef?.changeMode(DRAW_MODE_BY_TYPE[drawMode] as any);
        }, [addToast, disabled, drawMode, feature, onActivate, t]);

        const setAppliedFeature = useCallback<Dispatch<SetStateAction<string>>>(
            valueToApply => {
                setFeature(prevFeature => {
                    const nextFeature =
                        typeof valueToApply === 'function'
                            ? valueToApply(prevFeature)
                            : valueToApply;

                    return nextFeature;
                });
            },
            []
        );

        const resetGeometry = useCallback(() => {
            setFeature('');
            onDeactivate();
            recoverSettingsAfterDrawing();
        }, [onDeactivate, recoverSettingsAfterDrawing]);

        return (
            <div className="lambda-run-form__geometry">
                <label className="lambda-run-form__geometry-label">
                    {label}
                    {required ? <span className="lambda-run-form__asterisk">*</span> : null}
                </label>
                <div className="geometry-form-field">
                    <input
                        ref={inputRef}
                        className="geometry-form-field__input"
                        name={name}
                        type="text"
                        value={feature}
                        required={required}
                        readOnly
                    />
                    <div className="lambda-run-form__geometry-actions">
                        <Button
                            text={t(feature ? 'lambda-operations.geometry-selected' : 'forms.set-geometry-btn')}
                            onClick={startDrawing}
                            disabled={disabled || !!feature}
                            colorType="transparent"
                            testId="addNewGeometryBtn"
                        />
                        {feature ? (
                            <Button
                                text={t('lambda-operations.reset-geometry')}
                                onClick={resetGeometry}
                                disabled={disabled}
                                colorType="transparent"
                                testId="resetGeometryBtn"
                            />
                        ) : null}
                    </div>
                </div>
                {active && drawnFeatures.length ? (
                    <Portal targetNodeId="map-top-toolbar">
                        <EditGeometryToolbar
                            drawnFeatures={drawnFeatures}
                            setFeature={setAppliedFeature}
                            defaultValue={initialValue}
                        />
                    </Portal>
                ) : null}
            </div>
        );
    }
);

export default LambdaGeometryField;
